/**
 * Section for displaying and editing expense line items
 */

import React from 'react';
import { Box, Typography, Button, Stack, Divider } from '@mui/material';
import { Add as AddIcon, ContentCopy as CopyIcon } from '@mui/icons-material';
import type { ExpenseItem } from '../../../types/ExpenseItem';
import { formatCurrency } from '../../../utils/formatters';
import { ExpenseItemForm } from '../forms/ExpenseItemForm';
import { FinancialLine } from './FinancialLine';

interface ExpenseSectionProps {
  expenseItems: ExpenseItem[];
  totalExpenses: number;
  isEditing: boolean;
  formData: Record<string, unknown>;
  onUpdateField: (field: string, value: unknown) => void;
  onCopyExpenses?: () => Promise<void>;
  showCopyButton: boolean;
  onStartEdit?: (section: string, holdId: string) => Promise<void>;
  holdId?: string;
}

export const ExpenseSection: React.FC<ExpenseSectionProps> = ({
  expenseItems,
  totalExpenses,
  isEditing,
  formData,
  onUpdateField,
  onCopyExpenses,
  showCopyButton,
  onStartEdit,
  holdId,
}) => {
  const handleAddItem = () => {
    const currentItems = (formData.expenseItems as ExpenseItem[]) || expenseItems;
    onUpdateField('expenseItems', [...currentItems, { name: '', amount: 0 }]);
  };

  const handleUpdateItem = (index: number, updatedItem: ExpenseItem) => {
    const currentItems = (formData.expenseItems as ExpenseItem[]) || expenseItems;
    const newItems = [...currentItems];
    newItems[index] = updatedItem;
    onUpdateField('expenseItems', newItems);
  };

  const handleRemoveItem = (index: number) => {
    const currentItems = (formData.expenseItems as ExpenseItem[]) || expenseItems;
    const newItems = currentItems.filter((_, i) => i !== index);
    onUpdateField('expenseItems', newItems);
  };

  const displayItems = isEditing
    ? ((formData.expenseItems as ExpenseItem[]) || expenseItems)
    : expenseItems;

  const displayTotal = displayItems.reduce((sum, item) => sum + (item.amount || 0), 0);

  return (
    <Box>
      <Stack direction="row" useFlexGap justifyContent="space-between" alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="subtitle2">Expenses (This Date)</Typography>
        {!isEditing && showCopyButton && onCopyExpenses && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<CopyIcon />}
            onClick={() => void onCopyExpenses()}
          >
            Copy to All Dates
          </Button>
        )}
      </Stack>

      {isEditing ? (
        // Edit mode
        <Stack spacing={2}>
          {displayItems.map((item, index) => (
            <ExpenseItemForm
              key={index}
              item={item}
              onChange={(updated) => handleUpdateItem(index, updated)}
              onRemove={() => handleRemoveItem(index)}
            />
          ))}

          <Button
            variant="outlined"
            fullWidth
            startIcon={<AddIcon />}
            onClick={handleAddItem}
          >
            ADD EXPENSE ITEM
          </Button>

          <Divider sx={{ my: 2 }} />

          <FinancialLine label="Total:" value={formatCurrency(displayTotal)} total />
        </Stack>
      ) : (
        // View mode
        <Box
          onClick={() => {
            if (onStartEdit && holdId) {
              void onStartEdit('financial', holdId);
            }
          }}
          sx={{
            cursor: onStartEdit ? 'pointer' : 'default',
            '&:hover': onStartEdit ? {
              bgcolor: 'action.hover',
              borderRadius: 1,
            } : {},
            p: 1,
            mx: -1,
          }}
        >
          {displayItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No expenses recorded
            </Typography>
          ) : (
            <Stack spacing={1}>
              {displayItems.map((item, index) => (
                <FinancialLine key={index} label={item.name || 'Unnamed'} value={formatCurrency(item.amount || 0)} />
              ))}

              <Divider sx={{ my: 2 }} />

              <FinancialLine label="Total:" value={formatCurrency(totalExpenses)} total />
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
};
